import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import helpers from 'src/helpers';
import { addNotification } from 'src/actions/notificationActions';
import {
  tooManyFilesMessage,
  ATTACHMENTS_BUSY_MESSAGE,
  MIXED_CONTAINER_MESSAGE,
} from 'src/helpers/attachmentBudget';
import { rebalanceAttachments } from 'src/helpers/attachmentRebalance';
import {
  applyContactCard,
  containerForFile,
  templateDropsAttachments,
  templateAttachmentsForApply,
  TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE,
} from 'src/helpers/contactCardAttachments';
import {
  NOTIFICATION_STATUS,
  STORAGE,
  MESSAGES,
} from 'src/constants';

const { VCARD_FILENAME } = MESSAGES;

/* CON-1934 — the multi-attachment beta for an editor that saves a
 * DEFINITION (a smart action today; join messages, keywords and templates
 * next). Attachment 1 stays in the form's legacy field and its container
 * field; attachments 2..N live in `extrasField` (an array), which the editor
 * sends as-is on Save.
 *
 * Same behavior as the Scheduled Connections editor (CON-1929/1930): files
 * upload as they are picked, every add rebalances the whole set against the
 * MMS budget, a placeholder shows per file while it uploads, and a second
 * pick during an upload is turned away. Clearing file 1 promotes the next.
 * File 1 may live in another account's container (a shared definition, a
 * seeded cp-company-all image); the server brings every file into the
 * user's own container on Save, so the row keeps one container.
 * The contact card is always file 1 (CON-1934): with room left it is ADDED
 * in front and every file stays; when full it replaces file 1 only. Files
 * can be added beside it. The card lives in the user's own container, so the
 * server brings the others there on Save too.
 *
 * Outside the beta (or for PB texting) maxAttachments is 1, isMultiBeta is
 * false, and the editor keeps its own single-file handler unchanged.
 *
 * `setPendingUpload(value)` writes the editor's pending-upload slot (Redux):
 * false = "nothing to upload, keep the name in the form", which is what
 * every beta path leaves behind, because the file is already uploaded. */
export default function useDefinitionAttachments({
  formik,
  legacyField,
  containerField,
  extrasField,
  user,
  azAccountName,
  databaseBeingUsed,
  setPendingUpload = () => { },
  onChanged = () => { },
  enabled = true,
  // CON-1934 — for a Scheduled Connections row (scheduledMessages), whose
  // save does not copy files between accounts: adding a file or the card to
  // a first file in another account's storage is refused, as the Manually
  // Scheduled screen does, instead of being unified on Save.
  refuseForeignMix = false,
}) {
  const dispatch = useDispatch();
  const { mmsAttachmentPolicy } = useSelector((state) => state?.account || {});
  const { values, setFieldValue } = formik;

  const canSendMultiple = enabled && helpers.permissions.canSendMultipleAttachments(user);
  const maxAttachments = canSendMultiple
    ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1)
    : 1;
  const isMultiBeta = maxAttachments > 1;
  const totalBytes = Number(mmsAttachmentPolicy?.totalBytes) || 307200;

  const [pendingTiles, setPendingTiles] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const originalsRef = useRef(new Map());

  const first = values[legacyField] || null;
  const extras = Array.isArray(values[extrasField]) ? values[extrasField] : [];
  const ownContainer = helpers.azure.getUserContainerName(databaseBeingUsed, user?.id);

  const notify = (key, text, status) => dispatch(addNotification(key, text, status));
  const uploadFailed = () => notify('saveFile', 'There are some problems with file upload service.', NOTIFICATION_STATUS.ERROR);

  // A new file 1 was uploaded to the user's own container (NULL). An
  // unchanged file 1 keeps its container, even another account's: the server
  // copies it over on Save when the files would otherwise span two (CON-1934).
  const setFiles = (names, { keepContainerFor = null } = {}) => {
    const [head = null, ...rest] = names;
    setFieldValue(legacyField, head, false);
    if (!(keepContainerFor && head === keepContainerFor)) {
      setFieldValue(containerField, null, false);
    }
    setFieldValue(extrasField, rest, false);
    setPendingUpload(false);
    onChanged(true);
  };

  const refusesForeignMix = () => {
    const container = values[containerField];
    if (refuseForeignMix && first && container && container !== ownContainer) {
      notify('attachment-mixed', MIXED_CONTAINER_MESSAGE, NOTIFICATION_STATUS.ERROR);
      return true;
    }
    return false;
  };

  const runExclusive = async (tileCount, work) => {
    if (busyRef.current) {
      notify('attachment-busy', ATTACHMENTS_BUSY_MESSAGE, NOTIFICATION_STATUS.INFO);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setPendingTiles(tileCount);
    try {
      await work();
    } finally {
      busyRef.current = false;
      setBusy(false);
      setPendingTiles(0);
    }
  };

  // Where a kept file lives: a file added in this window in the user's own
  // container, a stored one in the row's (CON-1934: after the card moved a
  // stored file 1 behind it, that file is still in the row's container).
  const containerOfKept = (name) => (originalsRef.current.has(name)
    ? ownContainer
    : (values[containerField] || ownContainer));

  const rebalanceDeps = {
    getUrl: (name) => helpers.azure.getImageFileURL(name, user?.id, azAccountName, STORAGE.sas, databaseBeingUsed, containerForFile(name, containerOfKept(name)), true),
    upload: (file) => helpers.azure.loadFileToAzureAndGetStatusCode(
      { file, value: file.name },
      null,
      user?.id,
      azAccountName,
      STORAGE.sas,
      databaseBeingUsed,
      null,
      uploadFailed,
      () => { },
      false,
      'EO-ID',
      false,
    ),
  };

  const addFiles = async (fileUploads, { firstName = first, current = extras } = {}) => {
    // CON-1934 — the contact card is one of the files: it counts toward the
    // limit and, at its real size (about 70 KB with a photo), toward the
    // budget. It is never recompressed and stays file 1.
    const existing = [firstName, ...current].filter(Boolean);
    const room = maxAttachments - existing.length;
    if (room <= 0) {
      notify('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.ERROR);
      return;
    }
    let uploads = fileUploads;
    if (uploads.length > room) {
      notify('attachment-count', tooManyFilesMessage(maxAttachments), NOTIFICATION_STATUS.WARNING);
      uploads = uploads.slice(0, room);
    }
    const result = await rebalanceAttachments({
      existing: existing.map((name) => ({ name, original: originalsRef.current.get(name) })),
      newFiles: uploads.map((entry) => entry.file),
      totalBytes,
      deps: rebalanceDeps,
    });
    if (!result.ok) {
      notify('attachment-size', result.error, NOTIFICATION_STATUS.ERROR);
      return;
    }
    result.originals.forEach((file, name) => originalsRef.current.set(name, file));
    setFiles(result.names, { keepContainerFor: firstName });
  };

  /* CON-1934 — choosing the contact card beside files: the new set is
   * re-planned against the budget like an add (the card a fixed file at its
   * real size, pictures shrunk to share what is left). Over budget, the
   * budget message shows and nothing changes. */
  const applyCardToSet = async (names) => {
    const result = await rebalanceAttachments({
      existing: names.map((name) => ({ name, original: originalsRef.current.get(name) })),
      newFiles: [],
      totalBytes,
      deps: rebalanceDeps,
    });
    if (!result.ok) {
      notify('attachment-size', result.error, NOTIFICATION_STATUS.ERROR);
      return;
    }
    result.originals.forEach((file, name) => originalsRef.current.set(name, file));
    setFiles(result.names, { keepContainerFor: VCARD_FILENAME });
  };

  /* AttachmentField's direct API, beta only. Returns true when it handled
   * the change; false hands it back to the editor's single-file handler. */
  const handleFileChange = ({
    attachment: newAttachment,
    fileUpload: newFileUpload,
    fileUploads: newFileUploads,
    isContactCard,
  }) => {
    if (!isMultiBeta) return false;
    if (isContactCard) {
      if (!first) {
        setFieldValue(extrasField, [], false);
        return false; // the editor's own contact-card branch: the card alone
      }
      // CON-1934 — beside files: added in front (room left) or in place of
      // file 1 (full). Every other file stays, and so does the container
      // they are in; the server moves them to the card's on Save. The set is
      // re-planned against the budget with the card at its real size.
      if (refusesForeignMix()) return true;
      const next = applyContactCard({ first, extras, maxAttachments });
      runExclusive(0, () => applyCardToSet([next.first, ...next.extras]));
      return true;
    }
    if (newAttachment === null && newFileUpload === null) {
      if (extras.length) {
        const [next, ...rest] = extras;
        setFieldValue(legacyField, next, false);
        // Files added here are in the user's own container. A stored file
        // (one the contact card moved behind it) stays in the row's.
        if ([next, ...rest].every((name) => originalsRef.current.has(name))) {
          setFieldValue(containerField, null, false);
        }
        setFieldValue(extrasField, rest, false);
        setPendingUpload(false);
        onChanged(true);
        return true;
      }
      return false; // the editor's own removal
    }
    if (!newFileUpload) return false;
    const picked = newFileUploads?.length ? newFileUploads : [newFileUpload];
    // CON-1934 — a file picked beside the contact card is added too.
    if (first) {
      if (refusesForeignMix()) return true;
      const room = maxAttachments - (1 + extras.length);
      runExclusive(Math.max(0, Math.min(picked.length, room)), () => addFiles(picked));
      return true;
    }
    runExclusive(
      Math.min(picked.length, maxAttachments),
      () => addFiles(picked, { firstName: null, current: [] }),
    );
    return true;
  };

  const removeAdditional = (index) => {
    setFieldValue(extrasField, extras.filter((_, i) => i !== index), false);
    onChanged(true);
  };

  /* For Save: the field to send, beta only. Absent outside the beta, so the
   * request is exactly what it was. */
  const extrasForSave = () => (isMultiBeta
    ? { [extrasField]: extras.length ? extras : null }
    : {});

  const fieldProps = isMultiBeta ? {
    maxAttachments,
    additionalAttachments: extras,
    onRemoveAdditional: removeAdditional,
    pendingCount: pendingTiles,
    // Added in this window = uploaded to the user's own container.
    additionalAttachmentContainerOf: containerOfKept,
  } : {};

  /* CON-1934 — for the editor's template Apply, which replaces every
   * attachment: in the beta, say so when that takes files away. CON-1936:
   * files the template brings itself are not "taken away". */
  const noticeIfTemplateDropsFiles = (templateAttachment, templateExtras = []) => {
    if (isMultiBeta && templateDropsAttachments({
      maxAttachments, current: [first, ...extras], templateAttachment, templateExtras,
    })) {
      notify('template-attachments', TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE, NOTIFICATION_STATUS.INFO);
    }
  };

  /* CON-1936 — the files a template brings into this editor: attachment 1
   * and, in the beta, its files 2..N. */
  const templateFiles = (template) => templateAttachmentsForApply({ template, maxAttachments });

  return {
    isMultiBeta,
    maxAttachments,
    busy,
    handleFileChange,
    extrasForSave,
    fieldProps,
    noticeIfTemplateDropsFiles,
    templateFiles,
  };
}

/* For an editor's Save, outside the component that owns the hook: the extras
 * field to send in the beta, nothing otherwise. */
export function definitionExtrasPayload({
  user, mmsAttachmentPolicy, extrasField, extras,
}) {
  const canSendMultiple = helpers.permissions.canSendMultipleAttachments(user);
  const max = canSendMultiple ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1) : 1;
  if (max <= 1) return {};
  const list = Array.isArray(extras) ? extras.filter(Boolean) : [];
  return { [extrasField]: list.length ? list : null };
}

/* CON-1934 — the same notice for an editor's template Apply that lives
 * outside the component owning the hook. True when the notice should show. */
export function definitionTemplateDropsFiles({
  user, mmsAttachmentPolicy, current, templateAttachment, templateExtras = [],
}) {
  const canSendMultiple = helpers.permissions.canSendMultipleAttachments(user);
  const max = canSendMultiple ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1) : 1;
  return templateDropsAttachments({
    maxAttachments: max, current, templateAttachment, templateExtras,
  });
}

/* CON-1936 — the files a template brings in, for an editor's Apply that
 * lives outside the component owning the hook: { first, extras }. */
export function definitionTemplateFiles({ user, mmsAttachmentPolicy, template }) {
  const canSendMultiple = helpers.permissions.canSendMultipleAttachments(user);
  const max = canSendMultiple ? Math.max(1, Number(mmsAttachmentPolicy?.maxAttachments) || 1) : 1;
  return templateAttachmentsForApply({ template, maxAttachments: max });
}

export { TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE };
